import React, { useState, useEffect, useContext } from 'react';
import propTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import moment from 'moment';
import './HabitsColumn.scss';
import { openModal, setModalData } from '~/Redux/Modules/Modal';
import { HABIT_CREATE_EDIT_MODAL } from '~/Config/Modals';
import HabitContext from './HabitContext';
import HabitItem from './HabitItem';
import LastPlaceholderHabit from './LastPlaceholderHabit';

const HabitsColumn = ({ habits, start, trackedHabits }) => {
  const [localHabits, setHabits] = useState([]);
  const [updatedPlaceholderHabitsToDisplay, setUpdatedPlaceholderHabitsToDisplay] = useState([]);
  const [sortedTrackedHabits, setSortedTrackedHabits] = useState([]);
  const dispatch = useDispatch();

  const habitState = useContext(HabitContext);

  useEffect(() => {
    const updatedSortedTrackedHabits = habits.map(habit => getSortedTrackedHabits(habit));
    setSortedTrackedHabits(updatedSortedTrackedHabits);
  }, [habits, start, trackedHabits]);

  const getSortedTrackedHabits = habit => {
    const startDate = moment(start).startOf('month');
    const endDate = moment(start).endOf('month');

    // Find tracked habits for the given habit.id in the array
    const trackedHabitsValues = trackedHabits.filter(
      trackedHabit => trackedHabit.habit_id === habit.id
    );
    const localSortedTrackedHabits = trackedHabitsValues
      .filter(
        trackedHabit =>
          (moment(trackedHabit.track_date).isSame(startDate) ||
            moment(trackedHabit.track_date).isAfter(startDate)) &&
          (moment(trackedHabit.track_date).isBefore(endDate) ||
            moment(trackedHabit.track_date).isSame(endDate))
      )
      .sort((a, b) => moment(a.track_date).diff(moment(b.track_date)));
    return localSortedTrackedHabits;
  };

  // Function to generate placeholder habits
  const generatePlaceholderHabits = () => {
    const leftSide = document.getElementsByClassName('habit-left-side')[0];
    const center = document.getElementsByClassName('habit-center')[0];

    if (leftSide) {
      const leftSideHeight = leftSide.getBoundingClientRect().height;
      // Define the height of each habit row
      const habitRowHeight = 30;
      const existingHabitsHeight = localHabits.length * habitRowHeight;
      const availableHeight = Math.max(leftSideHeight - existingHabitsHeight - 136); //the last number corresponds to height of other elements and padding in div
      // Calculate the number of rows to generate
      let numRowsToGenerate = Math.floor(availableHeight / habitRowHeight);
      // Ensure there is at least one placeholder
      numRowsToGenerate = Math.max(numRowsToGenerate, 1);
      const updatedPlaceholderHabitsToDisplay = Array.from(
        { length: numRowsToGenerate },
        (_, index) => index + 1
      );
      setUpdatedPlaceholderHabitsToDisplay(updatedPlaceholderHabitsToDisplay);
      if (habitState.placeholderRows.length !== updatedPlaceholderHabitsToDisplay.length) {
        habitState.update({ placeholderRows: updatedPlaceholderHabitsToDisplay });
      }
    } else {
      console.log('no element with class habit-left-side found');
    }
    if (center) {
      const centerHeight = center.getBoundingClientRect().height;
      // Define the height of each habit row
      const habitRowHeight = 30;
      const existingHabitsHeight = localHabits.length * habitRowHeight;
      const availableHeight = Math.max(centerHeight - existingHabitsHeight - 40); //the last number corresponds to height of other elements and padding in div
      // Calculate the number of rows to generate
      let numRowsToGenerate = Math.floor(availableHeight / habitRowHeight);
      // Ensure there is at least one placeholder
      numRowsToGenerate = Math.max(numRowsToGenerate, 1);
      const updatedPlaceholderHabitsToDisplay = Array.from(
        { length: numRowsToGenerate },
        (_, index) => index + 1
      );
      setUpdatedPlaceholderHabitsToDisplay(updatedPlaceholderHabitsToDisplay);
      if (habitState.placeholderRows.length !== updatedPlaceholderHabitsToDisplay.length) {
        habitState.update({ placeholderRows: updatedPlaceholderHabitsToDisplay });
      }
    } else {
      console.log('no element with class habit-center-side found');
    }
  };

  useEffect(() => {
    let isMounted = true; // A flag to track if the component is mounted
    let resizeListener; // Store the reference to the event listener

    const handleResize = () => {
      // Recalculate and update placeholder habits when the window is resized
      generatePlaceholderHabits();
    };

    if (updatedPlaceholderHabitsToDisplay.length === 0) {
      // Generate placeholder habits when they are empty
      generatePlaceholderHabits();
    }

    // Sorting habits can be done here too
    const sortedHabits = [...habits].sort((a, b) => {
      const aPriority = a.priority || Number.POSITIVE_INFINITY;
      const bPriority = b.priority || Number.POSITIVE_INFINITY;
      if (aPriority !== bPriority) {
        if (a.priority < b.priority) return -1;
        if (a.priority > b.priority) return 1;
      } else {
        if (a.start < b.start) return -1;
        if (a.start > b.start) return 1;
      }
      return 0;
    });

    const filteredHabits = sortedHabits.filter(habit => {
      const habitStart = moment(habit.start).startOf('day');
      const habitEnd = habit.end ? moment(habit.end) : null;

      const habitStartBeforeOrAtDate = habitStart.isSameOrBefore(start, 'minute');
      const habitNotAfterDate = !habitStart.isAfter(start);

      const habitEndAfterOrNotBeforeDate =
        !habitEnd || habitEnd.isAfter(start) || !habitEnd.isBefore(start);

      return habitStartBeforeOrAtDate && habitNotAfterDate && habitEndAfterOrNotBeforeDate;
    });

    if (isMounted) {
      setHabits(filteredHabits);
      generatePlaceholderHabits();
    }

    if (habitState.habitRows.length !== filteredHabits.length) {
      habitState.update({ habitRows: filteredHabits });
    }

    const isDifferent = habitState.habitRows.some(
      (habit, index) =>
        moment(habit.updated_at).valueOf() !== moment(filteredHabits[index]?.updated_at).valueOf()
    );
    if (isDifferent) {
      habitState.update({ habitRows: filteredHabits });
    }

    // Add event listener for window resize
    if (isMounted) {
      resizeListener = window.addEventListener('resize', handleResize);
    }

    // Cleanup function to set the "isMounted" flag to false when unmounting and remove the event listener
    return () => {
      isMounted = false;
      // Remove the event listener to prevent memory leaks
      if (resizeListener) {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [habits, start, habitState.habitRows]);

  // Listen for changes in habits and after setHabits and regenerate placeholder habits when it changes
  useEffect(() => {
    generatePlaceholderHabits();
  }, [habits, localHabits]);

  return (
    <div className="habits-column">
      {localHabits.map((habit, key) => (
        <HabitItem
          key={key}
          habit={habit}
          prevHabit={key > 0 ? localHabits[key - 1] : null}
          nextHabit={key < localHabits.length - 1 ? localHabits[key + 1] : null}
        />
      ))}
      <LastPlaceholderHabit
        prevHabit={localHabits.length ? localHabits[localHabits.length - 1] : null}
      />
      {updatedPlaceholderHabitsToDisplay.slice(0, -1).map(index => (
        <div
          key={index}
          className="habit-row empty not-first-child"
          onClick={() => {
            dispatch(setModalData({ habit: null }));
            dispatch(openModal(HABIT_CREATE_EDIT_MODAL));
          }}
        />
      ))}
    </div>
  );
};

HabitsColumn.propTypes = {
  habits: propTypes.array,
  start: propTypes.object,
  trackedHabits: propTypes.array,
};

const mapStateToProps = ({ DateRange, Habits, HabitTrackeds }) => {
  return {
    trackedHabits: HabitTrackeds.habitTrackedCollection.habitsTracked,
    habits: Habits.habitCollection.habits,
    start: DateRange.start,
  };
};

export default connect(mapStateToProps)(HabitsColumn);
